// components/HistoricoTab.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Box,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  History,
  Edit,
  Refresh,
  Business,
  CalendarToday,
  TrendingUp,
  Close,
  Delete
} from '@mui/icons-material';

import { axiosInstance } from '../utils/axios';
import CalculatorForm from './calculator/CalculatorForm';

const HistoricoTab = () => {
  const [calculos, setCalculos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingCalculation, setEditingCalculation] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [calculoToDelete, setCalculoToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Función para mostrar notificaciones
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Cargar cálculos al montar el componente
  useEffect(() => {
    fetchCalculos();
  }, []);

  // Función para obtener los cálculos desde la API
  const fetchCalculos = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/calculos');
      setCalculos(response.data);
    } catch (error) {
      console.error('Error al cargar cálculos:', error);
      showSnackbar('Error al cargar el histórico de cálculos', 'error');
      
      // Datos de ejemplo en caso de error de conexión
      setCalculos([
        {
          id: 1,
          cliente_id: 1,
          nombre_empresa: 'Empresa Demo S.A.',
          horas_servicio: 160.00,
          monto_base: 150000.00,
          porcentaje_sep: 5.0,
          porcentaje_oct: 10.0,
          porcentaje_nov: 20.0,
          porcentaje_dic: 30.0,
          porcentaje_ene: 15.0,
          created_at: '2025-01-15T10:30:00Z'
        },
        {
          id: 2,
          cliente_id: 2,
          nombre_empresa: 'Tecnología ABC',
          horas_servicio: 80.00,
          monto_base: 85000.00,
          porcentaje_sep: 8.0,
          porcentaje_oct: 12.0,
          porcentaje_nov: 15.0,
          porcentaje_dic: 25.0,
          porcentaje_ene: 10.0,
          created_at: '2025-01-14T15:45:00Z'
        },
        {
          id: 3,
          cliente_id: 1,
          nombre_empresa: 'Empresa Demo S.A.',
          horas_servicio: 160.00,
          monto_base: 175000.00,
          porcentaje_sep: 3.0,
          porcentaje_oct: 7.0,
          porcentaje_nov: 12.0,
          porcentaje_dic: 18.0,
          porcentaje_ene: 8.0,
          created_at: '2025-01-13T09:15:00Z'
        }
      ]);
      showSnackbar('Mostrando datos de ejemplo (sin conexión a API)', 'warning');
    } finally {
      setLoading(false);
    }
  };

  // Función placeholder para editar (ahora con funcionalidad completa)
  const handleEdit = (calculo) => {
    console.log('Editando cálculo:', calculo);
    setEditingCalculation(calculo);
    setEditDialogOpen(true);
  };

  // Función para manejar cuando se completa la edición
  const handleSaveComplete = (savedData) => {
    // Actualizar el cálculo en la lista local
    setCalculos(prev => prev.map(calc => 
      calc.id === savedData.id ? { ...calc, ...savedData } : calc
    ));
    
    // Cerrar el diálogo
    setEditDialogOpen(false);
    setEditingCalculation(null);
    
    // Refrescar la lista desde la API
    fetchCalculos();
    
    showSnackbar('Cálculo actualizado exitosamente', 'success');
  };

  // Función para cancelar la edición
  const handleCancelEdit = () => {
    setEditDialogOpen(false);
    setEditingCalculation(null);
  };

  // Función para abrir el diálogo de confirmación de eliminación
  const handleDeleteClick = (calculo) => {
    setCalculoToDelete(calculo);
    setDeleteDialogOpen(true);
  };

  // Función para confirmar la eliminación
  const handleConfirmDelete = async () => {
    if (!calculoToDelete) return;

    setLoading(true);
    try {
      await axiosInstance.delete(`/calculo/${calculoToDelete.id}`);
      
      // Actualizar la lista local
      setCalculos(prev => prev.filter(calc => calc.id !== calculoToDelete.id));
      
      showSnackbar('Cálculo eliminado exitosamente', 'success');
    } catch (error) {
      console.error('Error al eliminar cálculo:', error);
      showSnackbar('Error al eliminar el cálculo', 'error');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setCalculoToDelete(null);
    }
  };

  // Función para cancelar la eliminación
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCalculoToDelete(null);
  };

  // Función para formatear fecha
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-AR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Fecha inválida';
    }
  };

  // Función para formatear moneda
  const formatCurrency = (amount) => {
    if (!amount || isNaN(amount)) return '$0,00';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Función para calcular el porcentaje total (CORREGIDA)
  const calculateTotalPercentage = (calculo) => {
    if (!calculo) return '0.0';
    
    const sep = parseFloat(calculo.porcentaje_sep) || 0;
    const oct = parseFloat(calculo.porcentaje_oct) || 0;
    const nov = parseFloat(calculo.porcentaje_nov) || 0;
    const dic = parseFloat(calculo.porcentaje_dic) || 0;
    const ene = parseFloat(calculo.porcentaje_ene) || 0;
    
    const total = sep + oct + nov + dic + ene;
    
    return total.toFixed(1);
  };

  // Función para calcular el monto final estimado (CORREGIDA)
  const calculateFinalAmount = (calculo) => {
    if (!calculo || !calculo.monto_base) return 0;
    
    const totalPercentage = parseFloat(calculateTotalPercentage(calculo)) || 0;
    const montoBase = parseFloat(calculo.monto_base) || 0;
    const finalAmount = montoBase * (1 + totalPercentage / 100);
    
    return finalAmount;
  };

  // Función helper para validar si el cálculo tiene datos válidos
  const isValidCalculation = (calculo) => {
    return calculo && 
           calculo.id && 
           calculo.nombre_empresa && 
           calculo.monto_base && 
           !isNaN(calculo.monto_base);
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Cargando histórico de cálculos...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      {/* Encabezado */}
      <Paper elevation={3} sx={{ p: 3, mt: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}>
          <div>
            <Typography variant="h4" gutterBottom>
              📊 Histórico de Cálculos
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Gestiona y revisa todos los cálculos de aumentos progresivos realizados
            </Typography>
          </div>
          
          <Button
            variant="outlined"
            startIcon={loading ? <CircularProgress size={20} /> : <Refresh />}
            onClick={fetchCalculos}
            disabled={loading}
          >
            Actualizar
          </Button>
        </Box>
      </Paper>

      {/* Estadísticas rápidas */}
      <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography variant="h6" color="primary">
              {calculos.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cálculos totales
            </Typography>
          </CardContent>
        </Card>
        
        <Card sx={{ minWidth: 200 }}>
          <CardContent>
            <Typography variant="h6" color="success.main">
              {new Set(calculos.map(c => c.cliente_id)).size}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Clientes únicos
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tabla de cálculos */}
      <Paper elevation={3} sx={{ mt: 3 }}>
        {calculos.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <History sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No hay cálculos guardados
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Los cálculos se guardarán automáticamente cuando generes PDFs
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>ID</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    🏢 Empresa
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                    ⏰ Horas
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    💰 Monto Base
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                    📈 % Total
                  </TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>
                    💵 Monto Final
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                    📅 Fecha
                  </TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                    🔧 Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {calculos.filter(isValidCalculation).map((calculo, index) => (
                  <TableRow 
                    key={calculo.id}
                    sx={{ 
                      '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                  >
                    <TableCell>
                      <Chip 
                        label={`#${calculo.id}`} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business color="action" />
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {calculo.nombre_empresa || 'Sin nombre'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Cliente ID: {calculo.cliente_id || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Chip 
                        label={`${calculo.horas_servicio || 0} hs`} 
                        size="small" 
                        color="info" 
                        variant="outlined"
                      />
                    </TableCell>
                    
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium" color="success.main">
                        {formatCurrency(calculo.monto_base)}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Chip 
                        label={`+${calculateTotalPercentage(calculo)}%`}
                        size="small"
                        color="warning"
                        icon={<TrendingUp />}
                      />
                    </TableCell>
                    
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="bold" color="secondary.main">
                        {formatCurrency(calculateFinalAmount(calculo))}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <CalendarToday sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption">
                          {formatDate(calculo.created_at)}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Editar cálculo">
                          <IconButton 
                            color="primary" 
                            onClick={() => handleEdit(calculo)}
                            size="small"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title="Eliminar cálculo">
                          <IconButton 
                            color="error" 
                            onClick={() => handleDeleteClick(calculo)}
                            size="small"
                          >
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Diálogo de Edición */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCancelEdit}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Editando Cálculo ID: {editingCalculation?.id}
          </Typography>
          <IconButton onClick={handleCancelEdit} color="inherit">
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          {editingCalculation && (
            <CalculatorForm 
              editingCalculation={editingCalculation}
              onSaveComplete={handleSaveComplete}
              onCancel={handleCancelEdit}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo de Confirmación de Eliminación */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete color="error" />
          <Typography variant="h6">
            Confirmar Eliminación
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Esta acción no se puede deshacer
          </Alert>
          
          {calculoToDelete && (
            <Box>
              <Typography variant="body1" gutterBottom>
                ¿Estás seguro de que deseas eliminar este cálculo?
              </Typography>
              
              <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>ID:</strong> #{calculoToDelete.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Empresa:</strong> {calculoToDelete.nombre_empresa}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Monto Base:</strong> {formatCurrency(calculoToDelete.monto_base)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Fecha:</strong> {formatDate(calculoToDelete.created_at)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCancelDelete} 
            variant="outlined"
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            variant="contained" 
            color="error"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <Delete />}
          >
            {loading ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default HistoricoTab;